import { useState } from "react";
import { api, type Named } from "../lib/api.js";
import { Notice, Tabs, useAsync } from "../components/ui.js";
import type { PageProps } from "./Shell.js";

interface CrossAccess {
  id: string; from_group_id: string; to_group_id: string;
  from_name: string; to_name: string;
}

export default function Groups({ me, onToggleRail }: PageProps) {
  const [tab, setTab] = useState<"people" | "machines" | "cross">("people");
  const canWrite = me.permissions.includes("*") || me.permissions.includes("group:write");

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title"><h1>Groups</h1></div>
      </header>
      <div className="content">
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { id: "people", label: "People groups" },
            { id: "machines", label: "Machine groups" },
            { id: "cross", label: "Cross-group access" },
          ]}
        />
        {tab === "people" ? (
          <NamedList
            endpoint="/api/console/groups" canWrite={canWrite}
            title="People groups"
            hint="A group decides which people and machines its members can see."
          />
        ) : null}
        {tab === "machines" ? (
          <NamedList
            endpoint="/api/console/device-groups" canWrite={canWrite}
            title="Machine groups"
            hint="Group machines so permissions and settings profiles can target them together."
          />
        ) : null}
        {tab === "cross" ? <CrossGroup canWrite={canWrite} /> : null}
      </div>
    </>
  );
}

function NamedList({
  endpoint, canWrite, title, hint,
}: { endpoint: string; canWrite: boolean; title: string; hint: string }) {
  const list = useAsync(() => api.get<Named[]>(endpoint), [endpoint]);
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState("");

  async function add() {
    setError("");
    try {
      await api.post(endpoint, { name, note });
      setName(""); setNote(""); list.reload();
    } catch (e) { setError((e as Error).message); }
  }

  async function remove(id: string) {
    setError("");
    try { await api.del(`${endpoint}/${id}`); list.reload(); }
    catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="stack">
      {error ? <Notice>{error}</Notice> : null}
      <div className="panel">
        <div className="panel__head"><h2>{title}</h2></div>
        <div className="panel__body" style={{ padding: 0 }}>
          <p className="faint small" style={{ padding: "0.75rem 1rem 0" }}>{hint}</p>
          <table className="data">
            <thead><tr><th>Name</th><th>Note</th><th style={{ width: 80 }} /></tr></thead>
            <tbody>
              {(list.data ?? []).map((g) => (
                <tr key={g.id}>
                  <td>{g.name}</td>
                  <td className="muted">{g.note || <span className="faint">&mdash;</span>}</td>
                  <td className="actions">
                    {canWrite ? (
                      <button className="btn btn--danger btn--sm" onClick={() => remove(g.id)}>Delete</button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!list.data?.length ? (
                <tr><td colSpan={3} className="faint" style={{ padding: "1.25rem", textAlign: "center" }}>
                  Nothing here yet.
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {canWrite ? (
        <div className="panel">
          <div className="panel__body">
            <div className="row row--wrap" style={{ alignItems: "flex-end" }}>
              <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
                <label className="field__label">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="field" style={{ marginBottom: 0, flex: "2 1 240px" }}>
                <label className="field__label">Note</label>
                <input value={note} onChange={(e) => setNote(e.target.value)} />
              </div>
              <button className="btn btn--primary" onClick={add} disabled={!name.trim()}>Add group</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function CrossGroup({ canWrite }: { canWrite: boolean }) {
  const groups = useAsync(() => api.get<Named[]>("/api/console/groups"), []);
  const links = useAsync(() => api.get<CrossAccess[]>("/api/console/group-access"), []);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState("");

  async function add() {
    setError("");
    try {
      await api.post("/api/console/group-access", { from_group_id: from, to_group_id: to });
      links.reload();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="stack">
      {error ? <Notice>{error}</Notice> : null}
      <div className="panel">
        <div className="panel__head"><h2>Cross-group access</h2></div>
        <div className="panel__body" style={{ padding: 0 }}>
          <p className="faint small" style={{ padding: "0.75rem 1rem 0" }}>
            By default a person only sees their own group. Add a link to let one group
            see another&rsquo;s people and machines.
          </p>
          <table className="data">
            <thead><tr><th>Members of</th><th>Can see</th><th style={{ width: 80 }} /></tr></thead>
            <tbody>
              {(links.data ?? []).map((l) => (
                <tr key={l.id}>
                  <td>{l.from_name}</td>
                  <td>{l.to_name}</td>
                  <td className="actions">
                    {canWrite ? (
                      <button className="btn btn--danger btn--sm"
                        onClick={() => api.del(`/api/console/group-access/${l.id}`).then(links.reload)}>
                        Remove
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {!links.data?.length ? (
                <tr><td colSpan={3} className="faint" style={{ padding: "1.25rem", textAlign: "center" }}>
                  No links. Groups are isolated from each other.
                </td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {canWrite ? (
        <div className="panel"><div className="panel__body">
          <div className="row row--wrap" style={{ alignItems: "flex-end" }}>
            <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
              <label className="field__label">Members of</label>
              <select value={from} onChange={(e) => setFrom(e.target.value)}>
                <option value="">Choose a group</option>
                {(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0, flex: "1 1 180px" }}>
              <label className="field__label">Can see</label>
              <select value={to} onChange={(e) => setTo(e.target.value)}>
                <option value="">Choose a group</option>
                {(groups.data ?? []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <button className="btn btn--primary" onClick={add} disabled={!from || !to || from === to}>
              Add link
            </button>
          </div>
        </div></div>
      ) : null}
    </div>
  );
}
